import { Link } from 'react-router-dom';
import { researchReport } from '../data/researchReport';

export function EditorPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Sticky Navigation */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <Link
            to="/"
            className="text-blue-600 hover:text-blue-700 transition-colors duration-300 font-medium inline-flex items-center"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Directory
          </Link>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8 max-w-5xl">
        <article className="bg-white rounded-lg shadow-lg">
          {/* Report Header */}
          <header className="p-8 md:p-12 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 leading-tight">
              {researchReport.title}
            </h1>
            <p className="text-xl text-gray-700 mb-4">
              {researchReport.subtitle}
            </p>
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-white border border-gray-200">
                📚 {researchReport.metadata.sources} Sources
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-white border border-gray-200">
                🔍 {researchReport.metadata.vendorsAnalyzed} Vendors Analyzed
              </span>
              <span className="inline-flex items-center px-3 py-1 rounded-full bg-white border border-gray-200">
                📅 {researchReport.metadata.date}
              </span>
            </div>
          </header>

          {/* Table of Contents */}
          <nav className="p-8 md:p-12 border-b border-gray-200 bg-gray-50">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Table of Contents</h2>
            <ul className="space-y-2">
              {researchReport.sections.map((section, index) => (
                <li key={section.id}>
                  <a
                    href={`#${section.id}`}
                    className="text-blue-600 hover:text-blue-800 hover:underline transition-colors"
                  >
                    {section.title}
                  </a>
                  {section.subsections && (
                    <ul className="ml-6 mt-2 space-y-1">
                      {section.subsections.map((subsection, subIndex) => (
                        <li key={`${section.id}-sub-${subIndex}`}>
                          <a
                            href={`#${section.id}-${subIndex}`}
                            className="text-sm text-gray-600 hover:text-gray-800 hover:underline transition-colors"
                          >
                            {subsection.title}
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/* Report Content */}
          <div className="p-8 md:p-12">
            {researchReport.sections.map((section) => (
              <section key={section.id} id={section.id} className="mb-16 scroll-mt-20">
                <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 border-b-2 border-blue-500 pb-2">
                  {section.title}
                </h2>

                {section.content && (
                  <div className="prose max-w-none">
                    {section.content.split('\n\n').map((paragraph, pIndex) => {
                      // Check if paragraph is a list item
                      if (paragraph.startsWith('**') && paragraph.includes('**:')) {
                        return (
                          <div key={pIndex} className="mb-4 pl-4 border-l-4 border-blue-300 bg-blue-50 p-4 rounded">
                            <p className="text-gray-800 leading-relaxed"
                               dangerouslySetInnerHTML={{
                                 __html: paragraph
                                   .replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900">$1</strong>')
                               }}
                            />
                          </div>
                        );
                      }

                      // Regular paragraph
                      return (
                        <p
                          key={pIndex}
                          className="text-gray-700 leading-relaxed mb-4"
                          dangerouslySetInnerHTML={{
                            __html: paragraph
                              .replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900">$1</strong>')
                              .replace(/\n-/g, '<br/>-')
                          }}
                        />
                      );
                    })}
                  </div>
                )}

                {section.subsections && section.subsections.map((subsection, subIndex) => (
                  <div key={`${section.id}-sub-${subIndex}`} id={`${section.id}-${subIndex}`} className="mt-8 scroll-mt-20">
                    <h3 className="text-xl md:text-2xl font-semibold text-gray-800 mb-4">
                      {subsection.title}
                    </h3>
                    <div className="prose max-w-none">
                      {subsection.content.split('\n\n').map((paragraph, pIndex) => {
                        // Check if paragraph starts with **(...)**
                        if (paragraph.startsWith('**') && paragraph.includes('**:')) {
                          return (
                            <div key={pIndex} className="mb-4 pl-4 border-l-4 border-green-300 bg-green-50 p-4 rounded">
                              <p className="text-gray-800 leading-relaxed"
                                 dangerouslySetInnerHTML={{
                                   __html: paragraph
                                     .replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900">$1</strong>')
                                 }}
                              />
                            </div>
                          );
                        }

                        return (
                          <p
                            key={pIndex}
                            className="text-gray-700 leading-relaxed mb-4"
                            dangerouslySetInnerHTML={{
                              __html: paragraph
                                .replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-900">$1</strong>')
                            }}
                          />
                        );
                      })}
                    </div>
                  </div>
                ))}
              </section>
            ))}
          </div>

          {/* Footer */}
          <footer className="p-8 md:p-12 border-t border-gray-200 bg-gray-50">
            <div className="text-center">
              <p className="text-gray-600 mb-4">
                This research report is part of The Definitive D8 Directory
              </p>
              <Link
                to="/"
                className="inline-flex items-center px-6 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-300 shadow-md hover:shadow-lg"
              >
                View Vendor Directory
                <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          </footer>
        </article>
      </div>
    </div>
  );
}
